import type { TFunction } from "i18next";
import {
  analyzeAppPayloadSchema,
  analyzeQvdsPayloadSchema,
  auditGroupPayloadSchema,
  collectCapacityPayloadSchema,
  collectReloadsPayloadSchema,
  syncAppPayloadSchema,
  syncGroupPayloadSchema,
  syncMasterItemsPayloadSchema,
  syncVariablesPayloadSchema,
} from "@repo/shared";

import type { Job } from "@/lib/api/jobs";

export type JobScopeResolvers = {
  // Resolve a connection/group id to its display name (from the client caches).
  // Returns undefined when the id is unknown (cache miss or list not loaded).
  connectionName: (id: string) => string | undefined;
  groupName: (id: string) => string | undefined;
  t: TFunction;
};

// Collapse a list of names into a single segment: the first name, plus a
// "+N" suffix when there are more (so multi-app jobs stay on one line).
function summarizeNames(names: string[], t: TFunction): string | undefined {
  const unique = Array.from(new Set(names.filter(Boolean)));
  if (unique.length === 0) return undefined;
  if (unique.length === 1) return unique[0];
  return t("scope.more", { name: unique[0], count: unique.length - 1 });
}

// Build the per-type scope segments shown in the jobs list (e.g. `["H2J Prod",
// "Sales 2024"]`, rendered as "H2J Prod · Sales 2024"). The connection/group
// name comes from the client caches; the target comes from the job payload.
// Returns an empty array when there is nothing meaningful to show.
export function describeJobScope(job: Job, resolvers: JobScopeResolvers): string[] {
  const { connectionName, groupName, t } = resolvers;

  switch (job.type) {
    case "analyze_app": {
      const parsed = analyzeAppPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      return [connectionName(parsed.data.connectionId), parsed.data.appName].filter(
        Boolean,
      ) as string[];
    }
    case "analyze_qvds": {
      const parsed = analyzeQvdsPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      const spaces = parsed.data.scope.spaces;
      const spaceLabel =
        spaces.length === 0
          ? t("scope.allSpaces")
          : summarizeNames(
              spaces.map((s) => s.spaceName),
              t,
            );
      return [connectionName(parsed.data.connectionId), spaceLabel].filter(Boolean) as string[];
    }
    case "collect_reloads": {
      const parsed = collectReloadsPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      return [connectionName(parsed.data.connectionId)].filter(Boolean) as string[];
    }
    case "collect_capacity": {
      const parsed = collectCapacityPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      // Capacity is measured tenant-wide, so the connection IS the whole scope.
      return [connectionName(parsed.data.connectionId)].filter(Boolean) as string[];
    }
    case "sync_app": {
      const parsed = syncAppPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      const source = parsed.data.variableChanges?.[0] ?? parsed.data.masterItemChanges?.[0];
      if (!source) return [];
      return [connectionName(source.connectionId), source.appName ?? source.appId].filter(
        Boolean,
      ) as string[];
    }
    case "sync_variables": {
      const parsed = syncVariablesPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      const first = parsed.data.changes[0];
      if (!first) return [];
      const apps = summarizeNames(
        parsed.data.changes.map((c) => c.appName ?? c.appId),
        t,
      );
      return [connectionName(first.connectionId), apps].filter(Boolean) as string[];
    }
    case "sync_master_items": {
      const parsed = syncMasterItemsPayloadSchema.safeParse(job.payload);
      if (!parsed.success) return [];
      const first = parsed.data.changes[0];
      if (!first) return [];
      const apps = summarizeNames(
        parsed.data.changes.map((c) => c.appName ?? c.appId),
        t,
      );
      return [connectionName(first.connectionId), apps].filter(Boolean) as string[];
    }
    case "sync_group":
    case "audit_group": {
      const schema = job.type === "sync_group" ? syncGroupPayloadSchema : auditGroupPayloadSchema;
      const parsed = schema.safeParse(job.payload);
      if (!parsed.success) return [];
      return [groupName(parsed.data.groupId) ?? t("scope.group")];
    }
    case "sync_all_groups":
    case "audit_all_groups":
      return [t("scope.allGroups")];
    default:
      return [];
  }
}
